import type { MemberPermissions } from "@/hooks/useMemberPermissions";

export type MemberProfile = "gestor" | "comercial" | "suporte" | "custom";

export const MEMBER_PROFILE_LABELS: Record<MemberProfile, string> = {
  gestor: "Gestor",
  comercial: "Comercial",
  suporte: "Suporte",
  custom: "Personalizado",
};

export const MEMBER_PROFILE_DESCRIPTIONS: Record<MemberProfile, string> = {
  gestor:
    "Acesso amplo ao CRM, conversas, agendamentos e tarefas. Pode criar e editar funis.",
  comercial:
    "Foco em vendas. Vê CRM, conversas (inbox + suas) e agendamentos. Não gerencia funis.",
  suporte:
    "Foco em atendimento. Só vê conversas atribuídas a ele. Sem gestão de funis.",
  custom: "Defina manualmente cada permissão.",
};

type PresetPermissions = Omit<MemberPermissions, "member_profile">;

export const MEMBER_PROFILE_PRESETS: Record<
  Exclude<MemberProfile, "custom">,
  PresetPermissions
> = {
  gestor: {
    conversations: true,
    crm: true,
    agents: false,
    products: false,
    integrations: false,
    settings: false,
    appointments: true,
    dashboard: true,
    automations: false,
    tasks: true,
    deals: true,
    conversations_visibility: "workspace_inbox",
    appointments_calendar_visibility: "all",
    deals_visibility: "all",
    crm_manage_funnels: true,
  },
  comercial: {
    conversations: true,
    crm: true,
    agents: false,
    products: false,
    integrations: false,
    settings: false,
    appointments: true,
    dashboard: true,
    automations: false,
    tasks: true,
    deals: true,
    conversations_visibility: "workspace_inbox",
    appointments_calendar_visibility: "assigned_only",
    deals_visibility: "all",
    crm_manage_funnels: false,
  },
  suporte: {
    conversations: true,
    crm: false,
    agents: false,
    products: false,
    integrations: false,
    settings: false,
    appointments: true,
    dashboard: false,
    automations: false,
    tasks: true,
    deals: false,
    conversations_visibility: "assigned_only",
    appointments_calendar_visibility: "assigned_only",
    deals_visibility: "assigned_only",
    crm_manage_funnels: false,
  },
};

/** Detecta se as permissões atuais batem com algum preset; caso não, retorna 'custom'. */
export function detectMemberProfile(perms: MemberPermissions): MemberProfile {
  if (perms.member_profile && perms.member_profile !== "custom") {
    return perms.member_profile;
  }
  const keys: (keyof PresetPermissions)[] = [
    "conversations",
    "crm",
    "agents",
    "products",
    "integrations",
    "settings",
    "appointments",
    "dashboard",
    "automations",
    "tasks",
    "deals",
    "conversations_visibility",
    "appointments_calendar_visibility",
    "deals_visibility",
    "crm_manage_funnels",
  ];
  for (const profile of ["gestor", "comercial", "suporte"] as const) {
    const preset = MEMBER_PROFILE_PRESETS[profile];
    const match = keys.every((k) => {
      const a = (perms as unknown as Record<string, unknown>)[k];
      const b = (preset as Record<string, unknown>)[k];
      // tratar undefined como false para flags booleanas
      if (typeof b === "boolean") return Boolean(a) === b;
      return a === b;
    });
    if (match) return profile;
  }
  return "custom";
}

export function applyMemberProfile(
  profile: MemberProfile,
  current: MemberPermissions
): MemberPermissions {
  if (profile === "custom") {
    return { ...current, member_profile: "custom" };
  }
  return {
    ...current,
    ...MEMBER_PROFILE_PRESETS[profile],
    member_profile: profile,
  };
}
